import { Col, Row, Table } from "react-bootstrap";
import type { RebalanceType } from "../../../api/src/rebalances/rebalances.service";
import AllocationBar from "../components/AllocationBar/AllocationBar";
import AssetAllocationBarChart, {
  AssetAllocationSeries,
} from "../components/AssetAllocationBarChart";
import { naLabel } from "../lib/display";
import { formatCurrency } from "../lib/numbers";
import { getRiskAllocation } from "./lib";
import type { RiskAllocation } from "../../../api/src/securities/securities.service";

// Helper function to check if all risk allocation values are zero
const isRiskAllocationEmpty = (
  riskAllocation: RiskAllocation | undefined | null,
) => {
  if (!riskAllocation) return true;
  const excludeKeys = ["Cash", "Unknown", "cash", "unknown"];
  const riskValues = Object.entries(riskAllocation)
    .filter(([key]) => !excludeKeys.includes(key))
    .map(([, value]) => value);
  return riskValues.length === 0 || riskValues.every((v) => !v);
};

// Helper function to check if asset allocation data is empty (all allocations are zero)
const isAssetAllocationEmpty = (data: AssetAllocationSeries["data"]) => {
  return (
    !data ||
    data.length === 0 ||
    data.every((point) => !point || point.allocation === 0)
  );
};

interface RebalanceAccountInsightsDataOnlyProps {
  // Account basic info
  accountId: string;
  accountName: string;
  accountType?: string;
  modelName?: string;
  modelId?: number;

  // Financial data
  totalBalance: number;
  cashBalance: number;
  buyTotal: number;
  sellTotal: number;
  realizedGainLoss: number;

  // Manual rebalance specific data
  trades: Record<
    string,
    {
      shares: number | string;
      price: number | string;
      realized_gain_loss_st: number | string;
      realized_gain_loss_lt: number | string;
    }
  >;

  // Risk allocation data
  riskAllocation: {
    current: Record<string, number>;
    target: Record<string, number>;
    postRebalance: Record<string, number>;
  };

  // Asset allocation data - grouped like riskAllocation
  assetAllocation: {
    current?: AssetAllocationSeries["data"];
    target?: AssetAllocationSeries["data"];
    postRebalance?: AssetAllocationSeries["data"];
  };

  // Rebalance type
  rebalanceType: RebalanceType;
  raiseSpendCashAmount?: number;
}

const RebalanceAccountInsightsDataOnly = ({
  accountId,
  accountName,
  accountType = "Manual Rebalance",
  modelName,
  totalBalance,
  cashBalance,
  buyTotal,
  sellTotal,
  realizedGainLoss,
  riskAllocation,
  assetAllocation,
  rebalanceType,
  raiseSpendCashAmount,
}: RebalanceAccountInsightsDataOnlyProps) => {
  // Create asset allocation series for this account
  const assetAllocationSeries: AssetAllocationSeries[] = [
    {
      label: "Current",
      data: assetAllocation.current ?? [],
    },
    {
      label: "Post-Rebalance",
      data: assetAllocation.postRebalance ?? [],
    },
    // Only include Target if it has non-zero allocations
    ...(!isAssetAllocationEmpty(assetAllocation.target ?? [])
      ? [
          {
            label: "Target",
            data: assetAllocation.target ?? [],
          },
        ]
      : []),
  ];

  return (
    <>
      {/* Account Header */}
      <div className="account-header mb-4 pb-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">
              {accountName} ({accountId})
            </h5>
            <small className="text-muted">
              {accountType}
              {modelName && (
                <>
                  {" • "}
                  <span className="font-italic">{modelName}</span>
                </>
              )}
            </small>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto",
              gap: "0.25rem 0.5rem",
              justifyContent: "end",
            }}
          >
            <small className="text-muted text-end">Total AUM:</small>
            <span className="h6 mb-0">{formatCurrency(totalBalance)}</span>
            <small className="text-muted text-end">Last Trade:</small>
            <span className="h6 mb-0">{naLabel}</span>
          </div>
        </div>
      </div>

      {/* Risk Allocation */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="mb-3 d-flex align-items-end">
            <div
              style={{
                width: "120px",
                paddingRight: "12px",
                textAlign: "right",
              }}
            >
              <small className="text-muted">Current</small>
            </div>
            <div style={{ flex: "1" }}>
              <AllocationBar
                data={riskAllocation.current}
                converter={getRiskAllocation}
              />
            </div>
          </div>
          {!isRiskAllocationEmpty(riskAllocation.postRebalance) && (
            <div className="mb-3 d-flex align-items-end">
              <div
                style={{
                  width: "120px",
                  paddingRight: "12px",
                  textAlign: "right",
                }}
              >
                <small className="text-muted">Post-Rebalance</small>
              </div>
              <div style={{ flex: "1" }}>
                <AllocationBar
                  data={riskAllocation.postRebalance}
                  converter={getRiskAllocation}
                />
              </div>
            </div>
          )}
          {!isRiskAllocationEmpty(riskAllocation.target) && (
            <div className="mb-3 d-flex align-items-end">
              <div
                style={{
                  width: "120px",
                  paddingRight: "12px",
                  textAlign: "right",
                }}
              >
                <small className="text-muted">Target</small>
              </div>
              <div style={{ flex: "1" }}>
                <AllocationBar
                  data={riskAllocation.target}
                  converter={getRiskAllocation}
                />
              </div>
            </div>
          )}

          {/* Cash Flow and Trading Metrics Table */}
          <div className="mt-4">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                <tr>
                  <td
                    style={{
                      width: "250px",
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Cash (Estimated)
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(cashBalance)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Sell
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(Math.abs(sellTotal))}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Buy
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(buyTotal)}
                  </td>
                </tr>
                <tr style={{ borderTop: "1px solid #dee2e6" }}>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#495057",
                      padding: "8px 12px",
                    }}
                  >
                    Net
                  </td>
                  <td
                    className="text-end"
                    style={{
                      padding: "8px 12px",
                      fontWeight: 500,
                      color:
                        Math.abs(sellTotal) - buyTotal >= 0
                          ? "#28a745"
                          : "#dc3545",
                    }}
                  >
                    {formatCurrency(Math.abs(sellTotal) - buyTotal)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Gains
                  </td>
                  <td
                    className="text-end"
                    style={{
                      padding: "8px 12px",
                      color: realizedGainLoss >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {formatCurrency(realizedGainLoss)}
                  </td>
                </tr>
                {(rebalanceType === "raise_cash" ||
                  rebalanceType === "spend_cash") && (
                  <tr>
                    <td
                      style={{
                        fontWeight: 500,
                        color: "#6c757d",
                        padding: "8px 12px",
                      }}
                    >
                      {rebalanceType === "raise_cash"
                        ? "Raise Cash"
                        : "Spend Cash"}
                    </td>
                    <td className="text-end" style={{ padding: "8px 12px" }}>
                      {formatCurrency(raiseSpendCashAmount ?? 0)}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>
        <Col md={6}>
          <div>
            <AssetAllocationBarChart
              data={assetAllocationSeries}
              excludedSecuritiesPercentage={0} // No excluded securities for manual rebalance
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default RebalanceAccountInsightsDataOnly;
